import { useState, useEffect } from "react";
import { RiCloseLine, RiMailLine, RiFileExcel2Line } from "react-icons/ri";
import Typography from "../../Utility/Typography";
import Button from "../../Utility/Button/Button";
import DateRangePicker from "../../Utility/DatePickerRange/DatePickerRange";
import { post, getBlob } from "../../Services/ApiBase.jsx";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { showError } from "../../utils/toast";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";
import "../Clients/clients.css";

const getDefaultRange = () => {
  const now = new Date();
  const day = now.getDay();
  const diffToMonday = day === 0 ? -6 : 1 - day;
  const monday = new Date(now);
  monday.setDate(now.getDate() + diffToMonday);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  return {
    startDate: monday.toISOString().slice(0, 10),
    endDate: sunday.toISOString().slice(0, 10),
  };
};

/** Safe segment for download filenames (cross-platform). */
function slugClientNameForFilename(name) {
  const base = String(name ?? "client").trim() || "client";
  return base
    .toLowerCase()
    .replace(/[/\\:*?"<>|]+/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

/** hours-summary-<client>-<dateFrom>-to-<dateTo>.<csv|xlsx> */
function buildHoursSummaryDownloadName(clientName, dateFrom, dateTo, format = "xlsx") {
  const ext = format === "csv" ? "csv" : "xlsx";
  const slug = slugClientNameForFilename(clientName);
  const from = String(dateFrom ?? "").trim() || "unknown";
  const to = String(dateTo ?? "").trim() || "unknown";
  return `hours-summary-${slug}-${from}-to-${to}.${ext}`;
}

const ClientHoursSummaryModal = ({ isOpen, onClose, client }) => {
  const [dateRange, setDateRange] = useState(getDefaultRange());
  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState(null);

  useEffect(() => {
    if (isOpen) {
      setDateRange(getDefaultRange());
      setActionType(null);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!loading) onClose();
  };

  const handleDateRangeChange = (newRange) => {
    setDateRange(newRange || getDefaultRange());
  };

  const handleEmail = async () => {
    setLoading(true);
    setActionType("email");
    try {
      await post(API_ENDPOINTS.CLIENTS.HOURS_SUMMARY_EMAIL(client.id), {
        dateFrom: dateRange.startDate,
        dateTo: dateRange.endDate,
      });
      handleClose();
    } catch (err) {
      showError(getApiErrorMessage(err, "Failed to send email"));
      setActionType(null);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    setLoading(true);
    setActionType("export");
    try {
      const response = await getBlob(API_ENDPOINTS.CLIENTS.HOURS_SUMMARY_EXPORT(client.id), {
        dateFrom: dateRange.startDate,
        dateTo: dateRange.endDate,
        format: "xlsx",
      });
      const blob = response.data;
      const filename = buildHoursSummaryDownloadName(
        client?.clientName,
        dateRange.startDate,
        dateRange.endDate,
        "xlsx"
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      handleClose();
    } catch (err) {
      showError(getApiErrorMessage(err, "Export failed"));
      setActionType(null);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const clientName = client?.clientName || "Client";

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        className="bg-surface-primary rounded-lg shadow-lg w-full max-w-md relative"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-border-primary">
          <Typography variant="h5" className="font-semibold text-text-primary">
            Hours summary
          </Typography>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleClose}
            disabled={loading}
            className="!text-text-secondary hover:!text-text-primary transition-colors !border-0 !shadow-none !bg-transparent !p-0 !px-0 !py-0 !font-normal !inline-flex !items-center !justify-center disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Close"
          >
            <RiCloseLine className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <Typography variant="text2" className="text-text-secondary block">
            Choose a time range and send or export the hours summary for{" "}
            <strong className="text-text-primary">{clientName}</strong>.
          </Typography>

          <div>
            <Typography
              variant="text2"
              component="label"
              className="font-medium text-text-primary mb-2 block"
            >
              Time range
            </Typography>
            <div className="w-full">
              <DateRangePicker
                showIcon={true}
                value={dateRange}
                onChange={handleDateRangeChange}
                placeholder="Select date range (e.g. weekly or custom)"
              />
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-2">
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={loading || !dateRange?.startDate || !dateRange?.endDate}
              onClick={handleEmail}
              className="inline-flex items-center justify-center gap-2"
            >
              {actionType === "email" && loading ? (
                <>
                  <Typography variant="text2" className="sr-only" component="span"></Typography>
                  Sending…
                </>
              ) : (
                <>
                  <RiMailLine className="w-5 h-5" />
                  Email summary
                </>
              )}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="md"
              disabled={loading || !dateRange?.startDate || !dateRange?.endDate}
              onClick={handleExport}
              className="inline-flex items-center justify-center gap-2"
            >
              {actionType === "export" && loading ? (
                <>
                  <Typography variant="text2" className="sr-only" component="span"></Typography>
                  Exporting…
                </>
              ) : (
                <>
                  <RiFileExcel2Line className="w-5 h-5" />
                  Export
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientHoursSummaryModal;
